import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectFilteredVirtualCards,
  selectUsersFullPaginatedDataFromState,
  selectVirtualCardsFullPaginatedData,
} from 'src/app/AccountsVirtualCards/redux/accountsVirtualCards.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBoxAndLinks, {
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsFiltersBody,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch, useQueryParamArrayHandler } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { postReportVirtualCardsDownload } from 'src/redux/actions/data/reportDownloads.actions'
import { getVirtualCards } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFromState,
  selectVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFromState,
  selectVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const AccountsVirtualCards: React.FC = () => {
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const creditCardsActiveDirectEnabledCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount =
    useSelector(
      selectCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFromState,
    )
  const creditCardsActiveCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  )
  const virtualCardsActiveSourceUserOrViewerOrDelegateUserMeCount = useSelector(
    selectVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFromState,
  )
  const virtualCardsCardholderOrViewerMeActiveAndInactiveCount = useSelector(
    selectVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFromState,
  )
  const appName = useSelector(selectAppName)
  const queryParams = useSelector(selectQueryParams)
  const { analytics } = useAnalyticsContext()

  const virtualCardsFullPaginatedData = useSelector(
    selectVirtualCardsFullPaginatedData,
  )
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)
  const usersFullPaginatedData = useSelector(
    selectUsersFullPaginatedDataFromState,
  )

  const dispatch = useBackgroundDispatch()

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  const [selectedFilters, setSelectedFilters] = useState<
    VirtualCardsAnalyticsEventProperties | undefined
  >(undefined)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.VirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatistics,
      appStatisticsKeys.VirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatistics,
    ])
  }, [dispatchStatistics])

  const dispatchPostReportVirtualCardsDownload = (
    filters: VirtualCardsFiltersBody,
  ): void => {
    dispatch(
      postReportVirtualCardsDownload({
        ...filters,
        cardholderOrViewer: 'me',
      }),
    )
  }

  const fetchVirtualCards = (
    filtersBody?: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getVirtualCards({
        count: 10,
        issued: true,
        page: page,
        cardholderOrViewer: 'me',
        ...filtersBody,
      }),
    )
  }

  const infiniteScrollProps = {
    ...virtualCardsFullPaginatedData,
    fetch: fetchVirtualCards,
  }

  const readOnly =
    creditCardsActiveCanIssueVirtualCardsCount === 0 ||
    creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount === 0 ||
    creditCardsActiveDirectEnabledCanIssueVirtualCardsCount === 0

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('all cards filter applied', {
        card_user:
          selectedFilters.card_user && selectedFilters.card_user.length > 0,
        card_type:
          selectedFilters.card_type && selectedFilters.card_type.length > 0,
        status: selectedFilters.status && selectedFilters.status.length > 0,
        search: !!selectedFilters.search,
        deactivating_range: !!selectedFilters.deactivating_in,
      })
    }
  }, [selectedFilters])

  return !(
    statisticsLoaded.creditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
    statisticsLoaded.creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
    statisticsLoaded.creditCardsActiveCanIssueVirtualCardsCountStatisticsLoaded &&
    statisticsLoaded.virtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsLoaded &&
    statisticsLoaded.virtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsLoaded
  ) ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <Helmet>
        <title>{`Virtual Cards | ${appName}`}</title>
        <meta name="description" content={`Virtual Cards page of ${appName}`} />
      </Helmet>
      <BackgroundPageContent noStackingIndex>
        <VirtualCardsBoxAndLinks
          onDownloadClick={dispatchPostReportVirtualCardsDownload}
          virtualCards={filteredVirtualCards}
          defaultTitle={
            <ObjectBoxTitle
              title={<Trns source={'Virtual Cards Created'} />}
              static={{
                label: <Trns source={'Active'} />,
                count:
                  virtualCardsActiveSourceUserOrViewerOrDelegateUserMeCount,
              }}
            />
          }
          emptyStateDesign={TableEmptyStateDesigns.AccountVirtualCardsActive}
          infiniteScrollProps={infiniteScrollProps}
          initialFilterValues={{
            sortBy: 'activeUpdatedAt',
            sortDescending: false,
            recipients: useQueryParamArrayHandler(queryParams.recipients),
            keyword: queryParams.search ? queryParams.search : '',
            statuses: useQueryParamArrayHandler(queryParams.statuses),
          }}
          totalCount={virtualCardsCardholderOrViewerMeActiveAndInactiveCount}
          readOnly={readOnly}
          usersFullPaginatedData={usersFullPaginatedData}
          setSelectedFilters={setSelectedFilters}
        />
      </BackgroundPageContent>
    </>
  )
}

export default AccountsVirtualCards
